import { inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { URL_VIEW_STATE_NAV } from './view-state-reuse.strategy';

/**
 * A page's view choices (status tab, period, view mode, group by, …) kept in
 * its URL query string, so a browser reload, the back button or a shared
 * link reopens the same view. Nothing is stored on the device: a page opened
 * fresh from the sidebar shows its defaults.
 *
 * A sidebar tap on the page already open also resets it to its defaults
 * (see `ViewStateReuseStrategy`).
 *
 * Call {@link injectUrlViewState} in a field initializer (an injection
 * context), read on init with `pick` / `get`, and `set` on every change.
 */
export interface UrlViewState {
  /** The raw `?key` value, or null when absent. */
  get(key: string): string | null;

  /** `?key` when it is one of `allowed`, else `fallback`. */
  pick<T extends string>(key: string, allowed: readonly T[], fallback: T): T;

  /**
   * Writes `?key=value` without a new history entry. A value equal to
   * `fallback` (the page's default), or null / undefined / '', drops the key
   * so a default view keeps a clean URL. Other query params are kept.
   */
  set(key: string, value: string | number | null | undefined, fallback?: string | number): void;
}

export function injectUrlViewState(): UrlViewState {
  const router = inject(Router);
  const route = inject(ActivatedRoute);
  // Writes made in the same tick (a type change that also resets the
  // category) go out as one navigation; separate merges would each start
  // from the old URL and the later one would undo the earlier.
  let pending: Record<string, string | null> | null = null;

  const flush = () => {
    const queryParams = pending;
    pending = null;
    if (!queryParams) return;
    void router.navigate([], {
      relativeTo: route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
      // The page's own write: ViewStateReuseStrategy keeps the page alive.
      state: { [URL_VIEW_STATE_NAV]: true },
    });
  };

  return {
    get: (key) => route.snapshot.queryParamMap.get(key),

    pick<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
      const value = route.snapshot.queryParamMap.get(key);
      return value !== null && (allowed as readonly string[]).includes(value)
        ? (value as T)
        : fallback;
    },

    set(key, value, fallback) {
      const dropped =
        value === null ||
        value === undefined ||
        value === '' ||
        (fallback !== undefined && String(value) === String(fallback));
      if (!pending) {
        pending = {};
        queueMicrotask(flush);
      }
      pending[key] = dropped ? null : String(value);
    },
  };
}
